// Composes the AR canvas capture with the logo overlay, then saves to Photos.
// The Info button and shutter button are HTML overlays excluded by design
// (they are never painted onto the WebGL canvas we capture).

import { requestCapture } from '../ar/captureModule';
import { logger } from './logger';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    el.onload  = () => resolve(el);
    el.onerror = reject;
    el.src     = src;
  });
}

function drawLogo(ctx: CanvasRenderingContext2D, w: number, h: number, logoImg: HTMLImageElement): void {
  void w;
  // Mirror the AR screen: logo.png at 64 CSS-px height, 12 CSS-px from top-left.
  // Scale proportionally to the canvas pixel dimensions (reference: 844 CSS-px tall viewport).
  const scale      = h / 844;
  const pad        = Math.round(12 * scale);
  const logoHeight = Math.round(64 * scale);
  const logoWidth  = Math.round(logoImg.naturalWidth * (logoHeight / logoImg.naturalHeight));
  ctx.drawImage(logoImg, pad, pad, logoWidth, logoHeight);
}

export async function captureAndSave(): Promise<void> {
  const dataUrl = await requestCapture();
  if (!dataUrl || dataUrl === 'data:,') {
    logger.warn('captureAndSave: empty capture result');
    return;
  }

  const [img, logoImg] = await Promise.all([
    loadImage(dataUrl),
    loadImage('/assets/logo.png'),
  ]);

  const off = document.createElement('canvas');
  off.width  = img.naturalWidth;
  off.height = img.naturalHeight;
  const ctx  = off.getContext('2d')!;

  ctx.drawImage(img, 0, 0);
  drawLogo(ctx, off.width, off.height, logoImg);

  const blob = await new Promise<Blob | null>((resolve) => {
    off.toBlob(resolve, 'image/jpeg', 0.92);
  });

  if (!blob) {
    logger.warn('captureAndSave: toBlob returned null');
    return;
  }

  await saveToPhotos(blob);
}

async function saveToPhotos(blob: Blob): Promise<void> {
  const file = new File([blob], 'whale-ar.jpg', { type: 'image/jpeg' });

  // Web Share API with files — triggers native "Save Image" on iOS/Android.
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Sag Harbor Whaling Museum AR' });
      return;
    } catch (err) {
      // AbortError means the user dismissed the share sheet — that's fine.
      if ((err as DOMException).name !== 'AbortError') {
        logger.warn('Share failed, falling back to download:', err);
      } else {
        return;
      }
    }
  }

  // Fallback: trigger a browser download.
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = 'whale-ar.jpg';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
